'use client'

import { useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { DEFAULT_LOCALE, type Locale } from '@/lib/i18n/config'
import { useMessages } from '@/lib/i18n/client'
import { getMessages } from '@/lib/i18n/messages'
import messages from './password-input.i18n'

type Messages = ReturnType<typeof getMessages<(typeof messages)['es']>>

interface Criterion {
  label: (t: Messages) => string
  test: (v: string) => boolean
}

const CRITERIA: Criterion[] = [
  { label: (t) => t.minLength, test: (v) => v.length >= 8 },
  { label: (t) => t.uppercase, test: (v) => /[A-Z]/.test(v) },
  { label: (t) => t.number, test: (v) => /[0-9]/.test(v) },
]

interface PasswordInputProps {
  id: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  showCriteria?: boolean
  required?: boolean
}

export function PasswordInput({
  id,
  value,
  onChange,
  placeholder = '••••••••',
  showCriteria = false,
  required = false,
}: PasswordInputProps) {
  const [visible, setVisible] = useState(false)
  const t = useMessages(messages)
  const showIndicators = showCriteria && value.length > 0

  return (
    <div className="space-y-2">
      <div className="relative">
        <Input
          id={id}
          type={visible ? 'text' : 'password'}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="pr-10"
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition hover:text-foreground"
          aria-label={visible ? t.hide : t.show}
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>

      {showIndicators && (
        <ul className="space-y-1">
          {CRITERIA.map((c) => {
            const ok = c.test(value)
            const label = c.label(t)
            return (
              <li
                key={label}
                className={`flex items-center gap-1.5 text-xs transition-colors ${
                  ok ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'
                }`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${ok ? 'bg-green-500' : 'bg-border'}`} />
                {label}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export function validatePassword(password: string, lang: Locale = DEFAULT_LOCALE): string | null {
  const t = getMessages(messages, lang)
  if (password.length < 8) return t.errorMinLength
  if (!/[A-Z]/.test(password)) return t.errorUppercase
  if (!/[0-9]/.test(password)) return t.errorNumber
  return null
}
